import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ActiveSession } from '@/hooks/useDashboardData';
import { Lightbulb, TrendingUp, Clock, BarChart3, PieChart as PieChartIcon, Activity, Sparkles } from 'lucide-react';
import { DecisionTrendsChart } from './charts/DecisionTrendsChart';
import { DecisionAreasChart } from './charts/DecisionAreasChart';
import { PhaseDistributionChart } from './charts/PhaseDistributionChart';
import { cn } from '@/lib/utils';
import { differenceInMinutes } from 'date-fns';

interface InsightsPanelProps {
  sessions: ActiveSession[];
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  embedded?: boolean;
}

export const InsightsPanel = ({ sessions, activeTab = 'overview', onTabChange, embedded = false }: InsightsPanelProps) => {
  const getMostCommonArea = () => {
    if (sessions.length === 0) return null;
    
    const areaCounts: Record<string, number> = {};
    sessions.forEach(session => {
      if (session.decision_type) {
        areaCounts[session.decision_type] = (areaCounts[session.decision_type] || 0) + 1;
      }
    });

    const mostCommon = Object.entries(areaCounts)
      .sort(([, a], [, b]) => b - a)[0];

    if (!mostCommon) return null;

    const percentage = Math.round((mostCommon[1] / sessions.length) * 100);
    return { area: mostCommon[0], percentage };
  };

  const getAvgTimeEstimate = (): string | null => {
    // Calculate average time from created_at to updated_at for sessions that progressed past phase 1
    const relevantSessions = sessions.filter(s => s.phase >= 2);
    
    if (relevantSessions.length === 0) {
      // Fallback: calculate time spent on all sessions so far
      if (sessions.length === 0) return null;
      
      const totalMinutes = sessions.reduce((sum, session) => {
        try {
          const created = new Date(session.created_at);
          const updated = new Date(session.updated_at);
          return sum + differenceInMinutes(updated, created);
        } catch {
          return sum;
        }
      }, 0);
      
      const avgMinutes = Math.round(totalMinutes / sessions.length);
      return formatDuration(avgMinutes);
    }
    
    // Calculate average time to reach phase 2
    const totalMinutes = relevantSessions.reduce((sum, session) => {
      try {
        const created = new Date(session.created_at);
        const updated = new Date(session.updated_at);
        return sum + differenceInMinutes(updated, created);
      } catch {
        return sum;
      }
    }, 0);
    
    const avgMinutes = Math.round(totalMinutes / relevantSessions.length);
    return formatDuration(avgMinutes);
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} Min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} Std`;
    }
    return `${hours} Std ${remainingMinutes} Min`;
  };

  const getPhase1Count = () => {
    return sessions.filter(s => s.phase === 1).length;
  };

  const mostCommon = getMostCommonArea();
  const phase1Count = getPhase1Count();

  if (sessions.length === 0) {
    return (
      <div className={cn(
        "relative overflow-hidden transition-all duration-500",
        !embedded && "rounded-2xl border border-border/50 bg-card p-6 hover:border-primary/20 hover:shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.12)]",
        embedded && "p-6"
      )}>
        {!embedded && <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" />}
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Insights</h3>
              <p className="text-sm text-muted-foreground">Muster und Empfehlungen erscheinen hier</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground text-center py-6 mt-4 border-t border-border/50">
            Starte deine ersten Entscheidungen, um personalisierte Insights zu erhalten
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "relative overflow-hidden transition-all duration-500",
      !embedded && "rounded-2xl border border-border/50 bg-card hover:border-primary/20 hover:shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.12)]"
    )}>
      {/* Background decorative elements */}
      {!embedded && (
        <>
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/3 rounded-full blur-2xl pointer-events-none" />
        </>
      )}

      {/* Highlight badge */}
      {!embedded && (
        <div className="absolute top-4 right-4 z-20">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
            <Sparkles className="w-3 h-3" />
            KI-gestützt
          </span>
        </div>
      )}

      <div className={cn("relative z-10", !embedded && "p-6", embedded && "p-6")}>
        {/* Header */}
        {!embedded && (
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Insights & Analytics</h3>
              <p className="text-sm text-muted-foreground">Muster, Statistiken & Empfehlungen</p>
            </div>
          </div>
        )}

        <Tabs 
          value={activeTab} 
          onValueChange={onTabChange}
          className={cn("space-y-4", embedded && "pt-0")}
        >
          <TabsList className="grid w-full grid-cols-2 bg-muted/50">
            <TabsTrigger value="overview" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">Übersicht</TabsTrigger>
            <TabsTrigger value="charts" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">Visualisierungen</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {mostCommon && (
              <div className="p-4 rounded-xl bg-gradient-to-br from-primary/5 to-transparent border border-primary/10 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <span>Muster-Erkennung</span>
                </div>
                <p className="text-sm text-muted-foreground pl-6">
                  {mostCommon.percentage}% deiner Entscheidungen waren im Bereich{' '}
                  <span className="font-semibold text-foreground">{mostCommon.area}</span>
                </p>
              </div>
            )}

            {getAvgTimeEstimate() && (
              <div className="p-4 rounded-xl bg-muted/30 border border-border/50 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Clock className="h-4 w-4 text-primary" />
                  <span>Durchschnittliche Zeit</span>
                </div>
                <p className="text-sm text-muted-foreground pl-6">
                  Ø {getAvgTimeEstimate()} bis Phase 2 Readiness
                </p>
              </div>
            )}

            {phase1Count > 0 && (
              <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 space-y-2">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-primary" />
                  <p className="text-sm font-medium">Empfehlung</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Du hast {phase1Count} {phase1Count === 1 ? 'Entscheidung' : 'Entscheidungen'} in Phase 1 – 
                  möchtest du {phase1Count === 1 ? 'diese' : 'eine davon'} abschließen?
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="charts" className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Activity className="h-4 w-4 text-primary" />
                <span>Entscheidungs-Trend (6 Monate)</span>
              </div>
              <DecisionTrendsChart sessions={sessions} />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <BarChart3 className="h-4 w-4 text-primary" />
                <span>Phasen-Verteilung</span>
              </div>
              <PhaseDistributionChart sessions={sessions} />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <PieChartIcon className="h-4 w-4 text-primary" />
                <span>Entscheidungsbereiche</span>
              </div>
              <DecisionAreasChart sessions={sessions} />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
